import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowUpRight, Check } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, RevealStagger, RevealItem } from "@/components/Reveal";
import { IMAGES, FEATURES } from "@/lib/site";
import {
  getCategories,
  getProductsByCategory,
} from "@/lib/catalog";

export default function Products() {
  const categories = getCategories();

  useEffect(() => {
    document.title = "Products — Stenzia Displays";
  }, []);

  return (
    <div data-testid="products-page">
      <PageHero
        overline="Product Range"
        title="Five display series, infinite configurations."
        subtitle="From premium sliders to advanced motion systems — every Stenzia series can be customised in size, colour and branding to your exact showroom spec."
        image={IMAGES.Product}
      />

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 gap-20 md:gap-24">
            {categories.map((cat, idx) => {
              const variants = getProductsByCategory(cat.slug);
              const isAccessories = cat.cards && cat.cards.length > 0;
              const isComingSoon = !isAccessories && (cat.coming_soon || variants.length === 0);
              if (isAccessories) {
                return (
                  <Reveal key={cat.slug}>
                    <article id={cat.slug} data-testid={`product-${cat.slug}`}>
                      <div className="overline text-stenzia-green">{cat.capacity}</div>
                      <div className="mt-3 flex items-start justify-between flex-wrap gap-4">
                        <h2 className="font-display font-light text-3xl md:text-4xl lg:text-5xl tracking-tighter text-charcoal leading-[1.05]">
                          {cat.name}
                        </h2>
                        {cat.tag && (
                          <span className="px-3 py-1 rounded-full text-[10px] tracking-[0.18em] uppercase font-semibold bg-charcoal text-white">{cat.tag}</span>
                        )}
                      </div>
                      <p className="mt-5 text-charcoal/65 leading-relaxed max-w-3xl">{cat.description}</p>
                      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {cat.cards.map((c) => (
                          <Link
                            key={c.slug}
                            to="/contact?product=accessories"
                            data-testid={`accessory-${c.slug}`}
                            className="group block rounded-2xl border border-charcoal/10 bg-white p-6 hover:border-stenzia-green/30 hover:-translate-y-0.5 hover:shadow-[0_18px_36px_-18px_rgba(0,0,0,0.18)] transition-all duration-400"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="font-display text-lg text-charcoal tracking-tight">{c.name}</div>
                              <ArrowUpRight size={16} className="text-charcoal/35 group-hover:text-stenzia-green group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                            </div>
                            <p className="mt-3 text-[13px] text-charcoal/60 leading-relaxed">{c.body}</p>
                          </Link>
                        ))}
                      </div>
                    </article>
                  </Reveal>
                );
              }
              return (
                <Reveal key={cat.slug}>
                  <article
                    id={cat.slug}
                    data-testid={`product-${cat.slug}`}
                    className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-center ${
                      idx % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""
                    }`}
                  >
                    <div className="lg:col-span-7 relative rounded-3xl overflow-hidden aspect-[4/3] bg-charcoal group shadow-xl">
                      <img
                        src={cat.image}
                        alt={cat.name}
                        loading="lazy"
                        className={`absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1500ms] ease-out ${isComingSoon ? "opacity-55" : ""}`}
                      />
                      <div className="absolute inset-0 bg-gradient-to-tr from-charcoal/40 via-transparent to-transparent" />
                      {cat.tag && (
                        <div className={`absolute top-5 left-5 px-3 py-1 rounded-full text-[10px] tracking-[0.18em] uppercase font-semibold shadow-lg ${isComingSoon ? "bg-white/90 text-charcoal" : "bg-stenzia-gold text-charcoal"}`}>
                          {cat.tag}
                        </div>
                      )}
                    </div>
                    <div className="lg:col-span-5">
                      <div className="overline text-stenzia-green">{cat.capacity}</div>
                      <h2 className="mt-4 font-display font-light text-3xl md:text-4xl lg:text-5xl tracking-tighter text-charcoal leading-[1.05]">
                        {cat.name}
                      </h2>
                      <p className="mt-5 text-charcoal/65 leading-relaxed">{cat.description}</p>

                      {!isComingSoon && (
                        <ul className="mt-6 space-y-2">
                          {["Heavy-duty steel frame", "Premium powder coating", "Custom sizing & branding"].map(
                            (f) => (
                              <li key={f} className="flex items-center gap-3 text-sm text-charcoal/75">
                                <Check size={16} className="text-stenzia-green" /> {f}
                              </li>
                            )
                          )}
                        </ul>
                      )}

                      {/* Variants — dynamic from catalog */}
                      {variants.length > 0 && (
                        <div className="mt-6 flex flex-wrap gap-2">
                          {variants.map((v) => (
                            <Link
                              key={v.slug}
                              to={`/products/${v.category}/${v.slug}`}
                              data-testid={`product-variant-${v.slug}`}
                              className="text-xs tracking-wide px-3 py-1.5 rounded-full border border-charcoal/15 text-charcoal/75 hover:border-stenzia-green hover:text-stenzia-green hover:bg-stenzia-green/5 hover:shadow-[0_4px_12px_-4px_rgba(11,107,83,0.25)] transition-all duration-300"
                            >
                              {v.name}
                            </Link>
                          ))}
                        </div>
                      )}

                      {isComingSoon ? (
                        <Link
                          to="/contact"
                          data-testid={`product-notify-${cat.slug}`}
                          className="group mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-charcoal/20 text-sm text-charcoal hover:border-charcoal hover:bg-charcoal hover:text-white transition-all duration-300"
                        >
                          Notify Me When Available
                          <ArrowUpRight
                            size={16}
                            className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                          />
                        </Link>
                      ) : (
                        <Link
                          to="/contact"
                          data-testid={`product-quote-${cat.slug}`}
                          className="group mt-8 btn-primary text-sm inline-flex"
                          style={{ padding: "12px 24px" }}
                        >
                          Request Quote
                          <ArrowUpRight
                            size={16}
                            className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                          />
                        </Link>
                      )}
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#F6F7F5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="overline text-stenzia-green">
              <span className="kbd-line" /> Engineering Standards
            </div>
            <h2 className="mt-6 font-display font-light text-4xl md:text-5xl tracking-tighter text-charcoal leading-[1.05] max-w-3xl">
              Every Stenzia product. The same uncompromising spec.
            </h2>
          </Reveal>
          <RevealStagger className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f) => (
              <RevealItem key={f.title}>
                <div className="h-full bg-white p-7 rounded-2xl border border-transparent hover:border-stenzia-green/20 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-500">
                  <div className="overline text-stenzia-green">{f.title}</div>
                  <p className="mt-3 text-sm text-charcoal/65 leading-relaxed">{f.body}</p>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>
    </div>
  );
}
